import * as React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ShellNavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Optional small pill shown after the label (e.g. "Soon"). */
  badge?: string;
  /**
   * Badge meaning:
   *  - "muted"  → just a count, no action implied (default)
   *  - "amber"  → waiting on a person, nothing downstream blocked
   *  - "red"    → blocks the next stage
   */
  badgeTone?: "muted" | "amber" | "red";
  /** Renders as a plain anchor opening in a new tab. */
  external?: boolean;
  /** Nested items, shown indented under this one (e.g. Scheduling → Grid). */
  children?: ShellNavItem[];
}

export interface ShellNavGroup {
  /** Optional label shown above the group (thin uppercase divider). */
  label?: string;
  /** Items rendered in this group. */
  items: ShellNavItem[];
}

interface AppShellProps {
  /** Primary nav groups (everyday items). */
  navGroups: ShellNavGroup[];
  /** Items pinned to the bottom of the sidebar (e.g. Settings). */
  footerNav?: ShellNavItem[];
  /** Brand / logo slot rendered at the top of the sidebar. */
  brand: React.ReactNode;
  /** Optional block rendered above the nav groups (e.g. event context header). */
  navHeader?: React.ReactNode;
  /** User dropdown rendered on the right of the header. */
  userMenu?: React.ReactNode;
  /** Optional title rendered in the header. */
  headerTitle?: React.ReactNode;
  children: React.ReactNode;
}


function useIsActive() {
  const { pathname } = useLocation();
  return React.useCallback(
    (href: string, exact = false) => {
      if (href === "#") return false;
      if (exact) return pathname === href;
      return pathname === href || pathname.startsWith(`${href}/`);
    },
    [pathname]
  );
}

const BADGE_TONE: Record<string, string> = {
  muted: "bg-muted text-muted-foreground",
  amber: "bg-amber-100 text-amber-900",
  red: "bg-rose-100 text-rose-900",
};

function NavItemButton({ item }: { item: ShellNavItem }) {
  const isActive = useIsActive();
  const Icon = item.icon;
  // Treat the dashboard "Home" link as exact so it isn't always active.
  const exact = item.href.split("/").filter(Boolean).length <= 1;
  const inner = (
    <>
      <Icon />
      <span>{item.label}</span>
      {item.badge && (
        <span
          className={cn(
            "ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-semibold tracking-wide",
            BADGE_TONE[item.badgeTone ?? "muted"]
          )}
        >
          {item.badge}
        </span>
      )}
    </>
  );
  const childActive = (item.children ?? []).some((c) => isActive(c.href, false));
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={item.external ? false : isActive(item.href, exact)}
        tooltip={item.label}
        className="rounded-xl"
      >
        {item.external ? (
          <a href={item.href} target="_blank" rel="noreferrer">
            {inner}
          </a>
        ) : (
          <NavLink to={item.href}>{inner}</NavLink>
        )}
      </SidebarMenuButton>

      {/* Nested items (e.g. Scheduling → Grid, Bulk edit …). Shown only while
          the parent branch is the one being used, so the top list stays short. */}
      {item.children && item.children.length > 0 &&
        (isActive(item.href, false) || childActive) && (
          <SidebarMenuSub className="group-data-[collapsible=icon]:hidden">
            {item.children.map((child) => (
              <SidebarMenuSubItem key={child.href + child.label}>
                <SidebarMenuSubButton asChild isActive={isActive(child.href, true)}>
                  <NavLink to={child.href}>
                    <span>{child.label}</span>
                    {child.badge && (
                      <span
                        className={cn(
                          "ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-semibold tracking-wide",
                          BADGE_TONE[child.badgeTone ?? "muted"]
                        )}
                      >
                        {child.badge}
                      </span>
                    )}
                  </NavLink>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        )}
    </SidebarMenuItem>
  );
}

export function AppShell({
  navGroups,
  footerNav,
  brand,
  navHeader,
  userMenu,
  headerTitle,
  children,
}: AppShellProps) {
  return (
    <SidebarProvider>
      <Sidebar
        collapsible="icon"
        className="border-r bg-sidebar [&_[data-sidebar=sidebar]]:bg-sidebar"
      >
        <SidebarHeader className="shrink-0 border-b border-sidebar-border p-0">
          <div className="flex h-12 items-center px-2 group-data-[collapsible=icon]:justify-center">
            {brand}
          </div>
          {navHeader && (
            <div className="group-data-[collapsible=icon]:hidden">{navHeader}</div>
          )}
        </SidebarHeader>

        <SidebarContent className="min-h-0 flex-1 overflow-y-auto overscroll-contain">


          {navGroups.map((group, gi) => (
            <SidebarGroup key={group.label ?? gi}>
              {group.label && (
                <SidebarGroupLabel className="text-[0.65rem] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {group.label}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  {group.items.map((item) => (
                    <NavItemButton key={item.href + item.label} item={item} />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarContent>

        <SidebarFooter className="shrink-0 border-t border-sidebar-border">
          {footerNav && footerNav.length > 0 && (
            <SidebarMenu>
              {footerNav.map((item) => (
                <NavItemButton key={item.href + item.label} item={item} />
              ))}
            </SidebarMenu>
          )}
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>

      {/* The shell owns the scroll: the inset is bounded to the viewport and
          the content pane is the single scroll container. Without min-h-0 a
          flex child defaults to min-height:auto, so the pane grew to its
          content instead of scrolling and long pages were simply cut off. */}
      <SidebarInset data-app-content className="h-svh overflow-hidden">
        <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b bg-background/80 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60 sm:px-6">
          <SidebarTrigger className="-ml-1 rounded-full" />
          <div className="min-w-0 flex-1 truncate text-base font-semibold">
            {headerTitle}
          </div>
          <div className={cn("flex items-center gap-2")}>{userMenu}</div>
        </header>

        {/* SidebarInset already renders the page's single <main> landmark. */}
        {/* The page pane scrolls vertically only. Anything genuinely too wide
            (a data table) must scroll inside its own container. */}
        <div data-app-scroll className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden">{children}</div>



      </SidebarInset>
    </SidebarProvider>
  );
}
